export type Role = "admin" | "barber" | "front_desk" | "booth_renter";

export interface Permissions {
  canViewCalendar: boolean;
  canViewClients: boolean;
  canViewSales: boolean;
  canViewReports: boolean;
  canViewProducts: boolean;
  canViewPayroll: boolean;
  canViewBoothRental: boolean;
  canViewBoothClients: boolean;
  canViewBoothSettings: boolean;
  canViewSettings: boolean;
  canViewDigitalForms: boolean;
  canViewWaitlistManager: boolean;
  canViewWalkIn: boolean;
  canViewAccounting: boolean;
  canViewAITelephony: boolean;
  canViewClientConnect: boolean;
  canViewMemberships: boolean;
  canViewMultiLocation: boolean;
  /** Calendar should only show this user's own bookings, with no staff selector. */
  ownBookingsOnly: boolean;
}

const ROLE_PERMISSIONS: Record<Role, Permissions> = {
  admin: {
    canViewCalendar: true, canViewClients: true, canViewSales: true, canViewReports: true,
    canViewProducts: true, canViewPayroll: true, canViewBoothRental: true, canViewBoothClients: false,
    canViewBoothSettings: false, canViewSettings: true, canViewDigitalForms: true, canViewWaitlistManager: true, canViewWalkIn: true,
    canViewAccounting: true, canViewAITelephony: true, canViewClientConnect: true, canViewMemberships: true, canViewMultiLocation: true,
    ownBookingsOnly: false,
  },
  front_desk: {
    canViewCalendar: true, canViewClients: true, canViewSales: true, canViewReports: false,
    canViewProducts: true, canViewPayroll: false, canViewBoothRental: false, canViewBoothClients: false,
    canViewBoothSettings: false, canViewSettings: true, canViewDigitalForms: true, canViewWaitlistManager: true, canViewWalkIn: true,
    canViewAccounting: false, canViewAITelephony: true, canViewClientConnect: true, canViewMemberships: true, canViewMultiLocation: false,
    ownBookingsOnly: false,
  },
  barber: {
    canViewCalendar: true, canViewClients: false, canViewSales: false, canViewReports: true,
    canViewProducts: false, canViewPayroll: false, canViewBoothRental: false, canViewBoothClients: false,
    canViewBoothSettings: false, canViewSettings: true, canViewDigitalForms: false, canViewWaitlistManager: false, canViewWalkIn: false,
    canViewAccounting: false, canViewAITelephony: false, canViewClientConnect: false, canViewMemberships: false, canViewMultiLocation: false,
    ownBookingsOnly: true,
  },
  booth_renter: {
    canViewCalendar: true, canViewClients: false, canViewSales: false, canViewReports: false,
    canViewProducts: false, canViewPayroll: false, canViewBoothRental: false, canViewBoothClients: true,
    canViewBoothSettings: true, canViewSettings: true, canViewDigitalForms: false, canViewWaitlistManager: false, canViewWalkIn: false,
    canViewAccounting: false, canViewAITelephony: false, canViewClientConnect: false, canViewMemberships: false, canViewMultiLocation: false,
    ownBookingsOnly: true,
  },
};

const DEFAULT_PERMISSIONS = ROLE_PERMISSIONS.barber;

export function getPermissions(role: string | null): Permissions {
  if (role && role in ROLE_PERMISSIONS) return ROLE_PERMISSIONS[role as Role];
  return DEFAULT_PERMISSIONS;
}

export function roleLabel(roleName: string | null | undefined): string {
  if (roleName === "admin") return "Owner";
  if (roleName === "barber") return "Service Provider";
  if (roleName === "front_desk") return "Front Desk";
  if (roleName === "booth_renter") return "Booth Renter";
  return roleName ?? "";
}

// Maps each admin page path to the permission key that gates it.
// Pages absent from this map (e.g. Settings) are open to any authenticated role.
export const PAGE_PERMISSION: Record<string, keyof Permissions> = {
  "/admin/calendar": "canViewCalendar",
  "/admin/clients": "canViewClients",
  "/admin/sales": "canViewSales",
  "/admin/reports": "canViewReports",
  "/admin/products": "canViewProducts",
  "/admin/payroll": "canViewPayroll",
  "/admin/booth-rental": "canViewBoothRental",
  "/admin/booth-clients": "canViewBoothClients",
  "/admin/booth-settings": "canViewBoothSettings",
  "/admin/forms": "canViewDigitalForms",
  "/admin/waitlist": "canViewWaitlistManager",
  "/admin/walkin": "canViewWalkIn",
  "/admin/accounting": "canViewAccounting",
  "/admin/telephony": "canViewAITelephony",
  "/admin/connect": "canViewClientConnect",
  "/admin/memberships": "canViewMemberships",
  "/admin/locations": "canViewMultiLocation",
};
